<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import EmptyState from '@/components/ui/empty-state/EmptyState.vue';
import GroupedEntityList from '@/components/workspace/GroupedEntityList.vue';
import { ChevronDown, ChevronRight, ClipboardPenLine } from 'lucide-vue-next';
import FormSubmissionAnswerList from '@/components/forms/FormSubmissionAnswerList.vue';
import { formatRelativeTime } from '~/lib/formatters';
import type { PortalFormRequest } from '~/types/models';

const props = withDefaults(defineProps<{
    items: PortalFormRequest[];
    loading?: boolean;
    limit?: number;
    showClient?: boolean;
    showForm?: boolean;
    emptyTitle?: string;
    emptyDescription?: string;
    contained?: boolean;
    detailsMode?: 'inline' | 'emit';
}>(), {
    loading: false,
    limit: 0,
    showClient: true,
    showForm: true,
    emptyTitle: 'No forms in progress',
    emptyDescription: 'Draft answers will appear here after a client starts an assigned portal form.',
    contained: true,
    detailsMode: 'inline',
});

const emit = defineEmits<{ view: [request: PortalFormRequest] }>();

const expandedIds = ref<string[]>([]);
const visibleItems = computed(() => props.limit > 0 ? props.items.slice(0, props.limit) : props.items);

function isExpanded(requestId: string): boolean {
    return expandedIds.value.includes(requestId);
}

function toggle(requestId: string) {
    expandedIds.value = isExpanded(requestId)
        ? expandedIds.value.filter(id => id !== requestId)
        : [...expandedIds.value, requestId];
}

function viewAnswers(request: PortalFormRequest) {
    if (props.detailsMode === 'emit') {
        emit('view', request);
        return;
    }
    toggle(request.id);
}

function progressLabel(request: PortalFormRequest): string {
    const count = request.draftAnswers?.length || 0;
    return `${count} answer${count === 1 ? '' : 's'} saved`;
}
</script>

<template>
    <GroupedEntityList :contained="contained" :empty="!loading && visibleItems.length === 0">
        <template #empty>
            <EmptyState embedded :title="emptyTitle" :description="emptyDescription">
                <template #icon>
                    <ClipboardPenLine class="size-8 text-muted-foreground/70" />
                </template>
            </EmptyState>
        </template>

        <div v-if="loading" class="py-8 text-center text-sm text-muted-foreground">
            Loading form progress...
        </div>

        <template v-else>
          <div v-for="request in visibleItems" :key="request.id">
            <div class="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="min-w-0 space-y-1.5">
                    <div class="flex flex-wrap items-center gap-2">
                        <p v-if="showForm" class="truncate text-sm font-medium text-foreground">{{ request.formName || 'Portal form' }}</p>
                        <Badge variant="secondary">In progress</Badge>
                        <Badge variant="outline">{{ progressLabel(request) }}</Badge>
                    </div>
                    <p class="text-xs text-muted-foreground">
                        <span v-if="showClient">{{ request.clientName || 'Client' }}</span>
                        <span v-if="showClient && request.projectName"> · </span>
                        <span v-if="request.projectName">{{ request.projectName }}</span>
                        <span v-if="showClient || request.projectName"> · </span>
                        Updated {{ formatRelativeTime(request.draftUpdatedAt || request.updatedAt) }}
                    </p>
                </div>

                <Button type="button" variant="ghost" size="sm" class="shrink-0" @click="viewAnswers(request)">
                    <ChevronDown v-if="isExpanded(request.id)" class="mr-2 size-4" />
                    <ChevronRight v-else class="mr-2 size-4" />
                    {{ detailsMode === 'inline' && isExpanded(request.id) ? 'Hide answers' : 'View answers' }}
                </Button>
            </div>

            <div v-if="detailsMode === 'inline' && isExpanded(request.id)" class="border-t border-border/50 bg-muted/20 px-4 py-3">
                <p class="mb-2 text-xs text-muted-foreground">
                    This is an autosaved draft. The client may still change these answers before submitting.
                </p>
                <FormSubmissionAnswerList
                    :answers="request.draftAnswers || []"
                    surface="plain"
                    empty-message="The client has opened this form but has not entered an answer yet."
                />
            </div>
          </div>
        </template>
    </GroupedEntityList>
</template>
